import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api, AdvisorMeeting } from "@/services/api";
import { usePabSession } from "@/hooks/usePabSession";
import { useActiveWorkspace } from "@/hooks/useActiveWorkspace";
import { Card } from "@/components/Card";
import { Button } from "@/components/Button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/Avatar";
import { getInitials, validateSafeUrl } from "@/utils/util";
import { Calendar, Clock, Users, FileText, Sparkles, ChevronRight } from "lucide-react";
import { Text } from "@/components/Typography";
import { cn } from "@/utils/cn";
import {
  isGoogleMeetRecordingUrl,
  formatSessionDate,
  formatTimeWithDuration,
} from "../utils/sessionUtils";
import { RecordedVideoingIcon } from "@/components/icons/icons";

interface SessionSummaryProps {
  meetingId: number;
  onClose: () => void;
  status: string;
  pabSessionId?: number; // Optional: for advisors who don't have a PAB session context
  meetingData?: AdvisorMeeting; // Optional: for advisors to avoid restricted meeting fetch
}

export const SessionSummary: React.FC<SessionSummaryProps> = ({
  meetingId,
  onClose,
  status,
  pabSessionId,
  meetingData,
}) => {
  const { session } = usePabSession();
  // Which workspace the user is *viewing as*, not which roles they hold. A
  // dual-role user reading their own PAB is an achiever here, and hasRole()
  // would call them an advisor -- disabling the fetch below and leaving the
  // drawer on its "Meeting not found" state with no request ever sent.
  const isAdvisor = useActiveWorkspace() === "advisor";
  
  // Use provided pabSessionId (for advisors) or session.id (for achievers)
  const sessionId = pabSessionId || session?.id;

  // Fetch meeting details
  const {
    data: meeting,
    isLoading: meetingLoading,
    error: meetingError,
  } = useQuery({
    queryKey: ["meeting", meetingId, sessionId],
    queryFn: async () => {
      return await api.pabSessions.getMeeting(sessionId!, meetingId);
    },
    enabled: !!meetingId && !!sessionId && !meetingData && !isAdvisor,
  });

  // Fetch meeting documents
  const {
    data: meetingDocuments,
    isLoading: documentsLoading,
    error: documentsError,
  } = useQuery({
    queryKey: ["meeting-documents", meetingId, sessionId],
    queryFn: async () => {
      return await api.pabSessions.getMeetingDocuments(sessionId!, meetingId);
    },
    enabled: !!meetingId && !!sessionId && !isAdvisor,
  });

  const meetingDetails = meetingData ?? meeting;

  // ✅ Always handle loading first
  if (!meetingData && (meetingLoading || !sessionId)) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // ✅ Then handle error / empty state
  if (!meetingDetails) {
    return (
      <div className="text-red-500 text-center py-8">
        Error loading session summary:{" "}
        {meetingError instanceof Error ? meetingError.message : "Meeting not found"}
      </div>
    );
  }
  const participants = meetingDetails.participants || [];
  const agendas = meetingDetails.meetingAgendas || [];
  const goals = meetingDetails.sessionPrepGoals || [];

  const participantRoleLabels: Record<string, string> = {
    ACHIEVER: "Achiever",
    ADVISOR: "Advisor",
  };

  return (
    // See SessionPrepPage's identical comment: h-full + overflow-y-auto (not
    // min-h-screen) so this scrolls correctly inside SessionsPage's height-
    // constrained tab container, matching the list view's proven pattern.
    <div className="h-full overflow-y-auto bg-neutral-50 dark:bg-surface-page-bg">
      <div className="w-full px-4 py-6 space-y-6 sm:px-6">
        <div className="max-w-[1368px] mx-auto space-y-6">
          {/* Breadcrumbs */}
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-pab-dark-subtle-text">
            <button onClick={onClose} className="hover:text-gray-900 dark:hover:text-white">
              <Text as="span">Sessions</Text>
            </button>
            <ChevronRight className="h-4 w-4" />
            <Text as="span" className="text-gray-900 dark:text-white font-medium">
              {meetingDetails.title} - Session Summary
            </Text>
          </div>

          {/* Main Content */}
          <div className="space-y-6">
            {/* Session Title and Details */}
            <div className="space-y-4">
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                {meetingDetails.title}
              </h1>
              <div className="flex flex-wrap items-center gap-2">
                {/* Type Badge */}
                <Text
                  as="span"
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border-gray-100 dark:border-surface-border border-[2px] text-gray-700 dark:text-text-gray"
                >
                  {meetingDetails.meetingFormat === "GROUP" ? "Group" : "1:1"}
                </Text>
                {/* Status Badge */}

                <Text
                  as="span"
                  className={cn(
                    "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium",
                    status === "upcoming" ? "session-status-upcoming" : "session-status-completed",
                  )}
                >
                  {status === "upcoming" ? "Upcoming" : "Completed"}
                </Text>

                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-pab-dark-subtle-text">
                  <Calendar className="h-4 w-4 text-primary-750" />
                  <Text as="span">{formatSessionDate(meetingDetails.startTime)}</Text>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock className="h-4 w-4 text-primary-750" />
                  <Text as="span">
                    {formatTimeWithDuration(meetingDetails.startTime, meetingDetails.endTime)}
                  </Text>
                </div>
              </div>
            </div>

            {/* Content Grid */}
            <div className="space-y-6">
              {/* Session Overview */}
              <Card className="bg-white dark:bg-session-card-dark-bg border border-gray-200 dark:border-session-card-dark-border rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="h-5 w-5 text-primary-750" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Session Overview
                  </h3>
                </div>
                {meetingDetails.description ? (
                  <Text
                    as="p"
                    className="text-base text-gray-700 dark:text-text-gray leading-relaxed"
                  >
                    {meetingDetails.description}
                  </Text>
                ) : (
                  <Text
                    as="p"
                    className="text-base text-gray-500 dark:text-pab-dark-subtle-text italic"
                  >
                    {meetingDetails.title}
                  </Text>
                )}
              </Card>

              {/* Attendees */}
              <Card className="bg-white dark:bg-session-card-dark-bg border border-gray-200 dark:border-session-card-dark-border rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="h-5 w-5 text-primary-750" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Attendees ({participants.length})
                  </h3>
                </div>
                <div className="flex flex-wrap items-center gap-6">
                  {participants.map((participant) => (
                    <div key={participant.id} className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage
                          src={participant.profileUrl || ""}
                          alt={participant.fullName}
                          className="grayscale"
                        />
                        <AvatarFallback className="bg-primary-100 text-primary-600 text-sm font-semibold">
                          {getInitials(participant.fullName).slice(0, 2)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col min-w-0">
                        <Text as="p" className="text-md font-medium text-gray-900 dark:text-white">
                          {participant.fullName}
                        </Text>
                        <Text
                          as="p"
                          className="text-sm text-gray-600 dark:text-pab-dark-subtle-text"
                        >
                          {participantRoleLabels[participant.role] ?? participant.role}
                        </Text>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Agenda Covered */}
              {agendas.length > 0 && (
                <Card className="bg-white dark:bg-session-card-dark-bg border border-gray-200 dark:border-session-card-dark-border rounded-lg p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <FileText className="h-5 w-5 text-primary-750" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Agenda Covered
                    </h3>
                  </div>
                  <div className="space-y-4">
                    {agendas.map((agenda, index) => (
                      <div
                        key={index}
                        className="bg-white dark:bg-surface-card-inner rounded-md border border-gray-200 dark:border-surface-border px-3 py-2 flex items-center justify-between gap-3"
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-50 flex items-center justify-center border border-green-200">
                            <Text
                              as="span"
                              className="text-sm font-semibold text-gray-500 dark:text-pab-dark-subtle-text"
                            >
                              {index + 1}
                            </Text>
                          </div>
                          <Text as="p" className="text-base text-gray-900 dark:text-white">
                            {agenda.description}
                          </Text>
                        </div>
                        {agenda.duration > 0 && (
                          <Text
                            as="span"
                            className="text-gray-600 dark:text-pab-dark-subtle-text text-sm flex-shrink-0"
                          >
                            {agenda.duration} min
                          </Text>
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Goals Discussed */}
              {goals.length > 0 && (
                <Card className="bg-white dark:bg-session-card-dark-bg border border-gray-200 dark:border-session-card-dark-border rounded-lg p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="h-5 w-5 text-primary-750" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Goals Discussed
                    </h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {goals.map((goal, index) => (
                      <Text
                        as="span"
                        key={index}
                        className="inline-flex items-center gap-1 border px-2 py-2 font-inter font-medium"
                        style={{
                          borderRadius: "var(--profile-pill-radius)",
                          backgroundColor: "var(--color-profile-skill-pill-bg)",
                          borderColor: "var(--color-profile-skill-pill-border)",
                          color: "var(--color-profile-skill-pill-text)",
                          fontSize: "var(--profile-pill-font-size)",
                          lineHeight: "var(--profile-pill-line-height)",
                        }}
                      >
                        {goal}
                      </Text>
                    ))}
                  </div>
                </Card>
              )}

              {/* Session Notes */}
              <Card className="bg-white dark:bg-session-card-dark-bg border border-gray-200 dark:border-session-card-dark-border rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="h-5 w-5 text-primary-750" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Session Notes
                  </h3>
                </div>
                <div className="flex flex-col gap-3">
                  {documentsLoading ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-pab-dark-subtle-text">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
                      <Text>Loading notes...</Text>
                    </div>
                  ) : documentsError ? (
                    <Text className="text-sm text-gray-500 dark:text-pab-dark-subtle-text">
                      No notes available
                    </Text>
                  ) : meetingDocuments ? (
                    <div className="flex flex-wrap gap-3">
                      {/* Notes by Gemini button */}
                      {meetingDocuments.fileUrl && (
                        <Button
                          variant="outlinePrimary"
                          size="md"
                          onClick={() => {
                            const safeUrl = validateSafeUrl(meetingDocuments.fileUrl);
                            if (safeUrl) {
                              window.open(safeUrl, "_blank");
                            }
                          }}
                          leftIcon={<FileText className="h-5 w-5 text-gray-700" />}
                          className="w-auto"
                        >
                          <Text className="text-sm font-medium text-gray-700 dark:text-text-gray">
                            Notes by Gemini
                          </Text>
                        </Button>
                      )}
                      {/* Attachment buttons */}
                      {meetingDocuments.attachmentUrls &&
                        meetingDocuments.attachmentUrls.length > 0 && (
                          <>
                            {(() => {
                              // Find Google Meet recording URL in attachmentUrls
                              const recordingUrl =
                                meetingDocuments.attachmentUrls.find((url) =>
                                  isGoogleMeetRecordingUrl(url),
                                ) || null;

                              // Filter out recordingUrl from attachments
                              const otherAttachments = recordingUrl
                                ? meetingDocuments.attachmentUrls.filter(
                                    (url) => url !== recordingUrl,
                                  )
                                : meetingDocuments.attachmentUrls;

                              return (
                                <>
                                  {/* Recording button with video icon and meeting title */}
                                  {recordingUrl && (
                                    <Button
                                      variant="outlinePrimary"
                                      size="sm"
                                      onClick={() => {
                                        const safeUrl = validateSafeUrl(recordingUrl);
                                        if (safeUrl) {
                                          window.open(safeUrl, "_blank");
                                        }
                                      }}
                                      leftIcon={
                                        <RecordedVideoingIcon className="h-6 w-6 text-gray-700" />
                                      }
                                      className="w-auto"
                                    >
                                      <Text className="text-sm font-medium text-gray-700">
                                        {meetingDetails.title}
                                      </Text>
                                    </Button>
                                  )}
                                  {/* Other attachment buttons */}
                                  {otherAttachments.map((url, index) => (
                                    <Button
                                      key={index}
                                      variant="outlinePrimary"
                                      size="md"
                                      onClick={() => {
                                        const safeUrl = validateSafeUrl(url);
                                        if (safeUrl) {
                                          window.open(safeUrl, "_blank");
                                        }
                                      }}
                                      leftIcon={<FileText className="h-5 w-5 text-gray-700" />}
                                      className="w-auto"
                                    >
                                      <Text className="text-sm font-medium text-gray-700">
                                        Attachment {index + 1}
                                      </Text>
                                    </Button>
                                  ))}
                                </>
                              );
                            })()}
                          </>
                        )}
                      {/* Show message if no fileUrl and no attachments */}
                      {!meetingDocuments.fileUrl &&
                        (!meetingDocuments.attachmentUrls ||
                          meetingDocuments.attachmentUrls.length === 0) && (
                          <Text className="text-sm text-gray-500 dark:text-pab-dark-subtle-text">
                            No notes available
                          </Text>
                        )}
                    </div>
                  ) : (
                    <Text className="text-sm text-gray-500 dark:text-pab-dark-subtle-text">
                      No notes available
                    </Text>
                  )}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
